<script setup>
import { ref, toRaw, watch } from "vue"
import { object, string } from 'yup'
import { personalizationRuleSchema, getValueSchemaForValueType } from '@/validation_schemas/portal/personalization'
import { buildFormState } from '@/utils/form'
import FormFieldInput from '@/components/FormFieldInput.vue'
import FormFieldError from '@/components/FormFieldError.vue'
import PersonalizationRuleNestedItemForm from '@/components/policies/PersonalizationRuleNestedItemForm.vue'
import { FORM_FIELD_ARRAY_VALUE_TYPES } from '@/constants'

const emit = defineEmits(['click:create', 'click:cancel'])

const props = defineProps({
  initialValues: {
    type: Object,
    default: null
  },
  actions: {
    type: Array,
    default: () => []
  },
  conditionFields: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const defaultInitialValues = {
  name: null,
  status: 'active',
  conditions: [],
  actions: [],
  published_at: null
}

const ruleFormState = ref(buildFormState({
    validationSchema: personalizationRuleSchema,
    initialValues: defaultInitialValues,
    fieldsMapping: {
      $name: 'name',
      $conditions: 'conditions',
      $actions: 'actions'
    }
}))

const conditionFormStates = ref([])
const actionFormStates = ref([])

initialize(props.initialValues || defaultInitialValues)

watch(() => props.initialValues, (newInitialValues) => initialize(newInitialValues || defaultInitialValues))

function initialize(initialValues) {
  conditionFormStates.value = buildConditionFormStates(initialValues)
  actionFormStates.value = buildActionFormStates(initialValues)
  ruleFormState.value.reset(initialValues)
}

function buildConditionFormStates(initialValues) {
  const conditions = initialValues?.conditions || []
  if (!conditions.length) {
    return [buildConditionFormState()]
  }
  return conditions.map(condition => (
    buildConditionFormState(ruleConditionToFormValues(condition))
  ))
}

function buildActionFormStates(initialValues) {
  const actions = initialValues?.actions || []
  if (!actions.length) {
    return [buildActionFormState()]
  }
  return actions.map(action => (
    buildActionFormState(ruleActionToFormValues(action))
  ))
}

function buildConditionFormState(
  initialValues = {
    conditionField: null,
    operator: null,
    value: null
  }
) {
  return buildFormState({
    validationSchema: object({
      conditionField: object().label('Condition field').required(),
      operator: object().label('Operator').required(),
      value: string().when('operator.value_type', getValueSchemaForValueType('Condition value'))
    }),
    initialValues,
    fieldsMapping: {
      $conditionField: 'conditionField',
      $operator: 'operator',
      $value: 'value'
    }
  })
}

function buildActionFormState(initialValues = { action: null, value: null }) {
  return buildFormState({
    validationSchema: object({
      action: object().label('Action').required(),
      value: string().when('action.value_type', getValueSchemaForValueType('Action value'))
    }),
    initialValues,
    fieldsMapping: {
      $action: 'action',
      $value: 'value'
    }
  })
}

function ruleConditionToFormValues({ name, operator, values }) {
  const conditionField = props.conditionFields.find(condField => condField.name === name)
  const selectedOperator = conditionField?.operators?.find(op => op.name === operator.name)
  return {
    conditionField: conditionField ? {
      name: conditionField.name,
      sdr_expression_field_path: conditionField.sdr_expression_field_path,
      operators: conditionField.operators || []
    } : null,
    operator: selectedOperator,
    value: getConditionFormValue(selectedOperator, values)
  }
}

function getConditionFormValue(selectedOperator, values) {
  if (FORM_FIELD_ARRAY_VALUE_TYPES.includes(selectedOperator.value_type)) {
    return values || []
  }
  return values.length ? values[0] : null
}

function formValuesToRuleCondition({ conditionField, operator, value }) {
  return {
      name: conditionField?.name,
      sdr_expression_field_path: conditionField?.sdr_expression_field_path,
      operator: operator ? { 
        name: operator.name,
        sdr_operator: operator.sdr_operator,
        sdr_function_name: operator.sdr_function_name
      } : null,
      values: getRuleConditionValues(value),
      value_type: operator?.value_type
    }
}

function getRuleConditionValues(value) {
  if (!Array.isArray(value)) {
    return value === null || typeof value === 'undefined' ? [] : [value]
  }
  return value
}

function ruleActionToFormValues({ name, value }) {
  return {
    action: props.actions.find(action => action.name === name),
    value
  }
}

function formValuesToRuleAction({ action, value }) {
  return {
    name: action?.name,
    settings_fields_paths: action?.settings_fields_paths,
    value,
    value_type: action?.value_type
  }
}

function getConditions() {
  return conditionFormStates.value.map(conditionFormState => (
    formValuesToRuleCondition(toRaw(conditionFormState.values))
  ))
}

function getActions() {
  return actionFormStates.value.map(actionFormState => (
    formValuesToRuleAction(toRaw(actionFormState.values))
  ))
}

function addAction() {
  actionFormStates.value.push(buildActionFormState())
}

function removeAction(index) {
  if (index > -1) {
    actionFormStates.value.splice(index, 1)
  }
}

function createRule() {
  conditionFormStates.value.forEach(conditionFormState => { conditionFormState.validate() })
  actionFormStates.value.forEach(actionFormState => { actionFormState.validate() })

  ruleFormState.value.fields.$conditions.inputValue = getConditions()
  ruleFormState.value.fields.$actions.inputValue = getActions()

  if (ruleFormState.value.validate()) {
    emit('click:create', toRaw(ruleFormState.value.values))
  }
}

function cancel() {
  emit('click:cancel')
}
</script>

<template>
  <div>
    <h3 class="mb-4">Create Rule</h3>
    <div class="row">
      <div class="col-4">
        <label class="form-label">Rule name *</label>
        <FormFieldInput
          type="text"
          class="form-control"
          :fieldModel="ruleFormState.fields.$name"
        />
        <FormFieldError :fieldModel="ruleFormState.fields.$name" />
      </div>
    </div>
    <div class="row mt-4">
      <div class="col-12">
         <label class="form-label">Criteria *</label>
      </div>
    </div>
    <div
      class="row mb-3"
      v-for="(conditionFormState, index) in conditionFormStates" :key="index"
    >
      <div class="col-10">
        <PersonalizationRuleNestedItemForm
          prefixLabel="If"
          nestedItemLabel="Condition"
          :nestedItemOptions="conditionFields"
          :nestedItemFieldModel="conditionFormState.fields.$conditionField"
          :operatorOptions="conditionFormState.fields.$conditionField.inputValue?.operators"
          :operatorFieldModel="conditionFormState.fields.$operator"
          :valueType="conditionFormState.fields.$operator.inputValue?.value_type"
          :valueFieldModel="conditionFormState.fields.$value"
          :disabled="disabled"
        />
        </div>
    </div>
    <hr />
    <div class="row mt-4">
      <div class="col-12">
         <label class="form-label">Actions *</label>
      </div>
    </div>
    <div
      class="row mb-3"
      v-for="(actionFormState, index) in actionFormStates" :key="index"
    >
      <div class="col-8">
        <PersonalizationRuleNestedItemForm
          prefixLabel="then"
          nestedItemLabel="Action"
          :nestedItemOptions="actions"
          :nestedItemFieldModel="actionFormState.fields.$action"
          :valueType="actionFormState.fields.$action.inputValue?.value_type"
          :valueFieldModel="actionFormState.fields.$value"
          :disabled="disabled"
          :showRemoveButton="index > 0"
          @click:remove="removeAction(index)"
        />
      </div>
      <div class="col-2 align-self-center" v-if="index === 0">
        <button
          class="btn btn-link fw-semibold text-decoration-none btn-add-action p-0"
          type="button"
          :disabled="disabled"
          @click="addAction()"
        >
          + ADD ACTION
        </button>
      </div>
    </div>
    <div class="row mt-5">
      <div class="col-8 d-flex gap-3">
        <button
          class="btn btn-primary ms-3"
          type="button"
          :disabled="(
            disabled ||
            ruleFormState.fields.$name.hasErrors ||
            conditionFormStates.some(conditionFormState => !conditionFormState.valid) ||
            actionFormStates.some(actionFormState => !actionFormState.valid)
          )"
          @click="createRule()"
        >
          CREATE
        </button>
        <button
          class="btn btn btn-link fw-semibold text-decoration-none"
          type="button"
          :disabled="false"
          @click="cancel()"
        >
          CANCEL
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.btn-add-action {
  min-width: unset;
}
</style>
